import { Fragment, useState } from 'react';
import {
  ResponsiveContainer, LineChart, Line, BarChart, Bar, XAxis, YAxis,
  CartesianGrid, Tooltip, Legend, Area, AreaChart, ReferenceLine, ReferenceDot,
} from 'recharts';
import { aedStr, ilsStr, eurStr, usdStr, pct, round, feeCaption, withResaleCosts, RESALE_DEFAULTS, areaBreakdown, perSqmStr } from '../lib/analysisEngine';
import { CHART_COLORS as C, compact, useCountUp } from '../lib/uiFormat';
import { useI18n } from '../lib/i18n';
import { StrSummaryCards, StrExpenseTable, StrategyBadge } from './StrAnalysis';
import ExecutiveSummary from './ExecutiveSummary';
import SensitivityMatrices from './SensitivityMatrices';
import CashFlowSection from './CashFlow';

// ---------- small presentational primitives ----------
function CountUp({ value, format }) {
  const v = useCountUp(value);
  return <>{format(v)}</>;
}
function Money({ aed, ils, eur, usd }) {
  return (
    <div>
      <div>{aedStr(aed)}</div>
      <div className="text-xs text-gray-400">{ilsStr((Number(aed) || 0) * ils)}</div>
      {eur != null && <div className="text-xs text-gray-400">{eurStr((Number(aed) || 0) * eur)}</div>}
      {usd != null && <div className="text-xs text-gray-400">{usdStr((Number(aed) || 0) * usd)}</div>}
    </div>
  );
}
// Big stat card for the "key numbers" strip. accent drives the value colour.
// When `ils` is provided, the ₪ amount is shown under the AED (height reserved so
// there is no layout shift when the live rate loads).
function StatCard({ label, value, format, sub, accent = 'gray', animate = true, ils, eur, usd }) {
  const c = {
    green: 'text-green-600', blue: 'text-blue-600', amber: 'text-amber-600',
    gray: 'text-gray-900', red: 'text-red-600',
  }[accent] || 'text-gray-900';
  return (
    <div className="bg-white rounded-[1.75rem] border border-black/[0.06] shadow-premium p-4 flex flex-col justify-between min-h-[104px]">
      <p className="text-[11px] uppercase tracking-wide text-gray-400 font-medium leading-tight">{label}</p>
      <div>
        <p className={`text-xl md:text-2xl font-bold tabular-nums ${c} leading-tight`}>
          {animate ? <CountUp value={value} format={format} /> : format(value)}
        </p>
        {ils != null && <p className="text-[11px] text-gray-400 tabular-nums min-h-[15px]">{ilsStr((Number(value) || 0) * ils)}</p>}
        {eur != null && <p className="text-[11px] text-gray-400 tabular-nums min-h-[15px]">{eurStr((Number(value) || 0) * eur)}</p>}
        {usd != null && <p className="text-[11px] text-gray-400 tabular-nums min-h-[15px]">{usdStr((Number(value) || 0) * usd)}</p>}
        {sub && <p className="text-[11px] text-gray-400 mt-0.5">{sub}</p>}
      </div>
    </div>
  );
}
// Headline-tier stat: number-first (institutional-fund style), small uppercase label
// beneath, a slim accent bar on top. Used for the 4 ROI/yield headline cards — one
// unified card system instead of four different gradient colours.
function HeadlineStat({ label, value, format, sub, accent = 'green' }) {
  const bar = { green: 'bg-primary', gold: 'bg-gold', navy: 'bg-[#1E3A5F]' }[accent] || 'bg-primary';
  const text = { green: 'text-primary', gold: 'text-gold', navy: 'text-[#1E3A5F]' }[accent] || 'text-primary';
  return (
    <div className="relative overflow-hidden bg-white rounded-[1.75rem] border border-black/[0.06] shadow-premium p-5">
      <span className={`absolute top-0 left-0 right-0 h-1 ${bar}`} aria-hidden="true" />
      <p className={`text-2xl md:text-3xl font-bold tabular-nums leading-tight ${text}`}><CountUp value={value} format={format} /></p>
      <p className="text-[11px] uppercase tracking-wide text-gray-400 font-semibold mt-1.5">{label}</p>
      {sub && <p className="text-[11px] text-gray-400 mt-1 leading-snug">{sub}</p>}
    </div>
  );
}
function MiniStat({ label, value, caption, hint }) {
  return (
    <div className="bg-gray-50 rounded-xl p-3 text-center" title={hint || undefined}>
      <p className="text-xs text-gray-400 mb-0.5">{label}</p>
      <p className="font-bold text-gray-800 tabular-nums">{value}</p>
      {caption && <p className="text-[10px] text-gray-400 mt-0.5 leading-snug">{caption}</p>}
    </div>
  );
}
function SummaryCard({ label, aed, ils, eur, usd, accent, caption }) {
  const c = { green: 'text-green-600', blue: 'text-blue-600', amber: 'text-amber-600', gray: 'text-gray-800' }[accent] || 'text-gray-800';
  return (
    <div className="rounded-xl border border-black/[0.06] p-4 bg-gray-50">
      <p className="text-xs text-gray-500 mb-1">{label}</p>
      <p className={`text-lg font-bold tabular-nums ${c}`}><CountUp value={aed} format={aedStr} /></p>
      <p className="text-xs text-gray-400"><CountUp value={(Number(aed) || 0) * ils} format={ilsStr} /></p>
      {eur != null && <p className="text-xs text-gray-400"><CountUp value={(Number(aed) || 0) * eur} format={eurStr} /></p>}
      {usd != null && <p className="text-xs text-gray-400"><CountUp value={(Number(aed) || 0) * usd} format={usdStr} /></p>}
      {caption && <p className="text-[10px] text-gray-400 mt-1 leading-snug">{caption}</p>}
    </div>
  );
}
function ChartCard({ title, subtitle, children }) {
  return (
    <div className="bg-white rounded-[1.75rem] p-4 md:p-5 shadow-premium border border-black/[0.06]">
      <div className="mb-3">
        <h3 className="font-semibold text-gray-800 text-sm">{title}</h3>
        {subtitle && <p className="text-[11px] text-gray-400 mt-0.5">{subtitle}</p>}
      </div>
      {children}
    </div>
  );
}

// ---------- chart tooltips (AED + ILS everywhere) ----------
function AedIlsTooltip({ active, payload, label, ils, eur, usd }) {
  if (!active || !payload || !payload.length) return null;
  return (
    <div className="bg-white/95 backdrop-blur border border-gray-200 rounded-xl shadow-lg px-3 py-2 text-xs min-w-[150px]">
      <p className="font-semibold text-gray-700 mb-1.5">{label}</p>
      {payload.filter(p => p.value != null).map((p, i) => (
        <div key={i} className="flex items-center gap-2 mb-1 last:mb-0">
          <span className="w-2 h-2 rounded-full shrink-0" style={{ background: p.color || p.stroke || p.fill }} />
          <span className="text-gray-500">{p.name}</span>
          <span className="ml-auto text-right leading-tight">
            <span className="font-medium text-gray-800 block">{aedStr(p.value)}</span>
            <span className="text-[10px] text-gray-400 block">{ilsStr(p.value * ils)}</span>
            {eur != null && <span className="text-[10px] text-gray-400 block">{eurStr(p.value * eur)}</span>}
            {usd != null && <span className="text-[10px] text-gray-400 block">{usdStr(p.value * usd)}</span>}
          </span>
        </div>
      ))}
    </div>
  );
}
function PctTooltip({ active, payload, label }) {
  if (!active || !payload || !payload.length) return null;
  return (
    <div className="bg-white/95 backdrop-blur border border-gray-200 rounded-xl shadow-lg px-3 py-2 text-xs min-w-[130px]">
      <p className="font-semibold text-gray-700 mb-1.5">{label}</p>
      {payload.filter(p => p.value != null).map((p, i) => (
        <div key={i} className="flex items-center gap-2 mb-1 last:mb-0">
          <span className="w-2 h-2 rounded-full shrink-0" style={{ background: p.color || p.stroke }} />
          <span className="text-gray-500">{p.name}</span>
          <span className="ml-auto font-medium text-gray-800">{pct(p.value)}</span>
        </div>
      ))}
    </div>
  );
}
const mkHandoverLabel = (value) => ({ value, position: 'insideTopRight', fontSize: 10, fill: C.handover, fontWeight: 600 });

// ---------- skeleton (loading / empty) ----------
function Sk({ className = '' }) { return <div className={`animate-pulse bg-gray-200/70 rounded-xl ${className}`} />; }
export function ReportSkeleton() {
  return (
    <div className="space-y-6">
      <Sk className="h-24 w-full" />
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
        {Array.from({ length: 5 }).map((_, i) => <Sk key={i} className="h-24" />)}
      </div>
      <Sk className="h-40 w-full" />
      <Sk className="h-72 w-full" />
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Sk className="h-64" /><Sk className="h-64" />
      </div>
    </div>
  );
}
function ChartEmpty() {
  const { t } = useI18n();
  return <div className="h-[260px] flex items-center justify-center text-sm text-gray-300">{t('no_data_to_plot')}</div>;
}

// Exit-scenario resale simulator: pick a holding period (handover .. horizon) and see
// the time-aware cash flow. All figures come from a.exitScenarios[n] (precomputed in the
// engine), so the slider recalculates instantly with no re-fetch.
function ExitScenarioCard({ a, ils, eur, usd, resale = RESALE_DEFAULTS, setResale, n, setN }) {
  const { t, fmtMonthYear: fmtMY } = useI18n();
  const scenarios = a.exitScenarios || [];
  if (!scenarios.length) return null;
  const s = withResaleCosts(scenarios[Math.min(n, scenarios.length - 1)], resale);
  const on = !!resale.enabled;
  const setField = (k, v) => setResale && setResale({ ...resale, [k]: v });
  const yrLabel = s.n === 0 ? t('at_handover_cap') : t('plus_n_yr', { n: s.n });
  const holdText = s.n === 0 ? t('at_handover_lc') : t('x_years_after_handover', { n: s.n });
  return (
    <div className="bg-gradient-to-br from-dark to-card rounded-[1.75rem] p-5 shadow-premium text-white">
      <div className="flex items-center justify-between gap-3 mb-1">
        <h3 className="font-semibold">{t('exit_scenario')}</h3>
        <span className="text-[11px] text-gray-400">{t('sell_x', { date: fmtMY(s.exitDate) })}</span>
      </div>
      <p className="text-xs text-gray-400 mb-4">{t('resale_simulator', { hold: holdText })}</p>

      <div className="mb-4">
        <div className="flex items-center justify-between mb-1.5">
          <span className="text-xs text-gray-300">{t('exit_after')}</span>
          <span className="text-xs font-semibold text-amber-300">{yrLabel}</span>
        </div>
        <input type="range" min={0} max={scenarios.length - 1} step={1} value={n}
          onChange={e => setN(Number(e.target.value))}
          className="w-full accent-amber-400 cursor-pointer" aria-label={t('exit_year_aria')} />
        <div className="flex justify-between text-[10px] text-gray-500 mt-1">
          <span>{t('handover')}</span><span>{t('plus_x_yr', { x: scenarios.length - 1 })}</span>
        </div>
      </div>

      {/* resale-costs toggle — broker commission + NOC fee, deducted when ON */}
      <div className="mb-4 rounded-xl bg-white/5 border border-white/10 p-3">
        <div className="flex items-center justify-between gap-2">
          <div>
            <p className="text-sm font-medium text-gray-200">{t('include_resale_costs')}</p>
            <p className="text-[11px] text-gray-500">{t('resale_costs_note')}</p>
          </div>
          <button type="button" onClick={() => setField('enabled', !on)} aria-pressed={on}
            className={`relative w-11 h-6 rounded-full transition shrink-0 ${on ? 'bg-amber-400' : 'bg-gray-600'}`}>
            <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition ${on ? 'translate-x-5' : ''}`}></span>
          </button>
        </div>
        {on && (
          <div className="grid grid-cols-2 gap-3 mt-3">
            <label className="text-[11px] text-gray-400 block">{t('broker_commission')}
              <div className="flex items-center gap-1 mt-1">
                <input type="number" step="0.1" value={resale.commissionPct} onChange={e => setField('commissionPct', e.target.value)}
                  className="w-full bg-white/10 border border-white/15 rounded-lg px-2 py-1 text-sm text-right text-white tabular-nums focus:outline-none focus:ring-1 focus:ring-amber-400" />
                <span className="text-gray-400">%</span>
              </div>
            </label>
            <label className="text-[11px] text-gray-400 block">{t('noc_fee')}
              <div className="flex items-center gap-1 mt-1">
                <span className="text-gray-400 text-[11px]">AED</span>
                <input type="number" value={resale.nocFee} onChange={e => setField('nocFee', e.target.value)}
                  className="w-full bg-white/10 border border-white/15 rounded-lg px-2 py-1 text-sm text-right text-white tabular-nums focus:outline-none focus:ring-1 focus:ring-amber-400" />
              </div>
            </label>
          </div>
        )}
      </div>

      <div className="space-y-3">
        <div className="flex justify-between text-sm"><span className="text-gray-400">{t('sale_value_at_exit', { market: a.hasMarket ? t('market_paren') : '' })}</span><span className="font-medium tabular-nums">{aedStr(s.saleValue)}</span></div>
        {s.resale && s.commission > 0 && (
          <div className="flex justify-between text-sm gap-3"><span className="text-gray-400">{t('less_broker_commission_x', { x: pct(resale.commissionPct) })}</span><span className="font-medium tabular-nums text-red-300 whitespace-nowrap">−{aedStr(s.commission)}</span></div>
        )}
        {s.resale && s.noc > 0 && (
          <div className="flex justify-between text-sm gap-3"><span className="text-gray-400">{t('less_noc_fee')}</span><span className="font-medium tabular-nums text-red-300 whitespace-nowrap">−{aedStr(s.noc)}</span></div>
        )}
        <div className="flex justify-between text-sm gap-3">
          <span className="text-gray-400">{t('less_total_purchase')} {s.remainingInstallments > 0 && <span className="text-gray-500 whitespace-nowrap">{t('x_installments_owed', { n: s.remainingInstallments })}</span>}</span>
          <span className="font-medium tabular-nums text-red-300 whitespace-nowrap">−{aedStr(s.purchasePrice)}</span>
        </div>
        {s.netRent > 0 && (
          <div className="flex justify-between text-sm gap-3">
            <span className="text-gray-400">{t('plus_net_rent_collected')} <span className="text-gray-500">{t('x_yr_holding', { n: s.n })}</span></span>
            <span className="font-medium tabular-nums text-green-300 whitespace-nowrap">+{aedStr(s.netRent)}</span>
          </div>
        )}
        <div className="border-t border-gray-700 pt-3 flex justify-between">
          <span className="text-gray-300">{t('exit_profit')} <span className="text-[11px] text-gray-500">{t('capital_gain_plus_rent')}</span></span>
          <div className="text-right">
            <span className="text-xl font-bold text-green-400 tabular-nums">{aedStr(s.profit)}</span>
            <div className="text-xs text-gray-400">{ilsStr(s.profit * ils)}</div>
            {eur != null && <div className="text-xs text-gray-400">{eurStr(s.profit * eur)}</div>}
            {usd != null && <div className="text-xs text-gray-400">{usdStr(s.profit * usd)}</div>}
          </div>
        </div>
        <div className="flex justify-between text-[11px] text-gray-500">
          <span>{t('net_after_fees_sc')}</span>
          <span className="tabular-nums">{aedStr(s.netAfter)}</span>
        </div>
        <div className="flex justify-between text-[11px] text-gray-500">
          <span>{t('cash_invested_pf')}</span>
          <span className="tabular-nums">{aedStr(s.cashInvested)}</span>
        </div>
        <div className="grid grid-cols-2 gap-2 pt-1">
          <div className="bg-white/10 rounded-xl p-3 text-center">
            <p className="text-[11px] text-gray-300">{t('roi_on_cash')}</p>
            <p className="text-xl font-bold text-amber-400 tabular-nums"><CountUp value={s.roi} format={pct} /></p>
          </div>
          <div className="bg-white/10 rounded-xl p-3 text-center">
            <p className="text-[11px] text-gray-300">{t('annualized_roi')}</p>
            <p className="text-xl font-bold text-amber-400 tabular-nums"><CountUp value={s.annualizedRoi} format={pct} /></p>
          </div>
        </div>
        <p className="text-[11px] text-gray-500 leading-snug">{s.resale ? t('resale_incl_note', { c: pct(resale.commissionPct), n: aedStr(resale.nocFee) }) : t('resale_excl_note')}</p>
      </div>
    </div>
  );
}

// Read-only analysis rendering shared by the internal page and the client share page.
// props: project, unit, analysis, ils, assumptions, showSummary, presentation, loading
export default function AnalysisReport({ project, unit, analysis, ils, eur, usd, assumptions, engineOpts, showSummary = false, presentation = false, loading = false, strategy, resale = RESALE_DEFAULTS, setResale, view = 'advanced', altAnalysis = null, exitYear, onExitYearChange }) {
  // Exit-year slider: normally local (uncontrolled) state, but a parent (e.g. the "Ask
  // about this deal" assistant) can drive it externally by passing exitYear/onExitYearChange
  // — same optional-controlled pattern as resale/setResale above — so the card + chat stay
  // in sync without a second source of truth.
  const [localExitN, setLocalExitN] = useState(0);
  const exitN = exitYear != null ? exitYear : localExitN;
  const setExitN = onExitYearChange || setLocalExitN;
  const { t, lang, dir, fmtMonthYear: fmtMY, tMilestone } = useI18n();
  if (loading) return <ReportSkeleton />;
  if (!analysis || !unit) return null;
  const a = analysis;
  const strat = strategy || a.strategy || 'ltr';
  const isStr = strat === 'str';
  const lite = view === 'lite';
  const handoverLabel = mkHandoverLabel(t('handover'));
  const legendAlign = dir === 'rtl' ? 'right' : 'left';
  // Flip @ Handover = exit scenario n=0 with the same resale-cost toggle as the
  // Exit Scenario card, so the two stay identical.
  const flip0 = withResaleCosts(a.exitScenarios?.[0], resale);
  const heroTitle = presentation ? 'text-2xl md:text-4xl' : 'text-xl md:text-3xl';

  // handover x-anchors for reference lines
  const handoverYear = a.atHandover?.year;
  const hoIdx = a.schedule.findIndex(r => r.isHandover);
  const payHandoverX = hoIdx >= 0 && a.paymentChart[hoIdx]
    ? (a.hasDates ? a.paymentChart[hoIdx].dateLabel : a.paymentChart[hoIdx].name) : null;

  const gainsData = a.years.filter(y => y.i >= a.constructionYears);
  const today = new Date();

  return (
    <>
      {/* ===== 1. HERO HEADER — project photo (or brand gradient) + gradient overlay ===== */}
      <section className="relative overflow-hidden rounded-[1.75rem] shadow-premium-lg mb-6 text-white">
        <div className="absolute inset-0 bg-cover bg-center"
          style={project.hero_image_url
            ? { backgroundImage: `url(${project.hero_image_url})` }
            : { background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 55%, #17513f 130%)' }} />
        <div className="absolute inset-0" style={{ background: project.hero_image_url
          ? 'linear-gradient(180deg, rgba(10,14,24,0.15) 0%, rgba(10,14,24,0.55) 60%, rgba(10,14,24,0.92) 100%)'
          : 'linear-gradient(180deg, rgba(10,14,24,0.05) 0%, rgba(10,14,24,0.30) 100%)' }} />
        <div className={`relative z-10 p-5 md:p-8 ${presentation ? 'pt-10 md:pt-16' : 'pt-8 md:pt-10'}`}>
          <div className="flex flex-wrap items-center gap-2 mb-3">
            <span className="text-[10px] uppercase tracking-widest font-semibold bg-white/10 backdrop-blur border border-white/20 rounded-full px-2.5 py-1">{t('off_plan')}</span>
            {project.is_phpp && <span className="text-[10px] uppercase tracking-widest font-semibold bg-white/10 backdrop-blur border border-white/20 rounded-full px-2.5 py-1" title={t('phpp_title')}>PHPP</span>}
            <StrategyBadge strategy={strat} />
            <span className="text-[10px] uppercase tracking-widest font-semibold bg-white/10 backdrop-blur border border-white/20 rounded-full px-2.5 py-1">{project.emirate === 'rak' ? t('rak') : t('dubai')}</span>
            {a.hasMarket && a.discountPct > 0 && (
              <span className="text-[10px] uppercase tracking-widest font-bold bg-primary text-white rounded-full px-2.5 py-1">{t('below_market', { x: pct(a.discountPct) })}</span>
            )}
          </div>
          <h1 className={`${heroTitle} font-bold tracking-tight leading-[1.05]`}>{project.project_name}</h1>
          <p className="text-white/70 text-sm md:text-base mt-2">
            {[project.developer_name, project.area, unit.unit_type, unit.unit_number ? t('unit_kw', { n: unit.unit_number }) : null].filter(Boolean).join(' · ')}
          </p>
          {a.internal > 0 && (
            <p className="text-white/45 text-xs mt-1">
              {areaBreakdown(a.internal, a.balcony, lang)}
              {/* compact price/sqft — full breakdown card moves to Advanced */}
              {lite && a.totalSqft > 0 && <span> · {aedStr(a.pricePerSqft)}{t('per_sqft_suffix')}</span>}
            </p>
          )}
          <div className="flex flex-wrap items-end justify-between gap-4 mt-6">
            {project.handover_date && (
              <div className="inline-flex items-center gap-1.5 bg-white/10 backdrop-blur border border-white/15 rounded-full pl-3 pr-3.5 py-1.5 text-xs text-white/60">
                <span>{t('handover')}</span>
                <span className="font-semibold text-white tabular-nums">{fmtMY(project.handover_date)}</span>
              </div>
            )}
            <div className={`text-right shrink-0 ${!project.handover_date ? 'ml-auto' : ''}`}>
              <p className={`${presentation ? 'text-3xl md:text-4xl' : 'text-2xl md:text-3xl'} font-bold tabular-nums leading-none`}>{aedStr(unit.price_aed)}</p>
              {/* ILS/EUR lines — reserve height so no shift when rate loads */}
              <p className="text-sm text-white/55 tabular-nums mt-1 min-h-[20px]">{ilsStr(unit.price_aed * ils)}</p>
              {eur != null && <p className="text-sm text-white/55 tabular-nums min-h-[20px]">{eurStr(unit.price_aed * eur)}</p>}
              {usd != null && <p className="text-sm text-white/55 tabular-nums min-h-[20px]">{usdStr(unit.price_aed * usd)}</p>}
            </div>
          </div>
        </div>
      </section>

      {/* ===== 1b. EXECUTIVE SUMMARY (auto-generated, both views) ===== */}
      <ExecutiveSummary analysis={a} ils={ils} eur={eur} usd={usd} />

      {/* ===== 2. KEY NUMBERS STRIP ===== */}
      <div className={`grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4 mb-6 ${a.hasPhpp ? 'lg:grid-cols-6' : 'lg:grid-cols-5'}`}>
        <StatCard label={t('until_handover')} value={a.bucketUntil} format={aedStr} accent="amber" ils={ils} eur={eur} usd={usd}
          sub={feeCaption(a.feeParts?.until, lang) || t('paid_during_construction')} />
        <StatCard label={t('on_handover')} value={a.bucketOnHandover} format={aedStr} accent="amber" ils={ils} eur={eur} usd={usd}
          sub={feeCaption(a.feeParts?.onHandover, lang) || (isStr ? t('incl_furnishing') : t('final_milestone'))} />
        {a.hasPhpp && (
          <StatCard label={t('post_handover_phpp')} value={a.bucketPost} format={aedStr} accent="amber" ils={ils} eur={eur} usd={usd}
            sub={t('phpp_installments_total')} />
        )}
        {isStr
          ? <StatCard label={t('str_net_income_yr')} value={a.str.net} format={aedStr} accent={a.str.net >= 0 ? 'green' : 'red'} sub={t('gross_x', { x: aedStr(a.str.gross) })} />
          : <StatCard label={t('expected_rent_yr')} value={a.startRent} format={aedStr} accent="green" sub={t('gross_yield_x', { x: pct(a.grossYield) })} />}
        <StatCard label={isStr ? t('str_net_yield') : t('net_rental_yield')} value={a.netYield} format={pct} accent={a.netYield >= 0 ? 'green' : 'red'} sub={isStr ? t('incl_furnishing') : t('after_service_charge')} />
        <StatCard label={t('roi_at_maturity_y', { y: a.maturityYears })} value={a.roiAt(a.atMaturity)} format={pct} accent={a.roiAt(a.atMaturity) >= 0 ? 'green' : 'red'} sub={t('capital_on_cash')} />
      </div>

      {/* ===== 2b. STR breakdown (short-term strategy only) ===== */}
      {isStr && a.str && (
        <>
          <StrSummaryCards a={a} ils={ils} eur={eur} usd={usd} />
          {!lite && <StrExpenseTable a={a} ils={ils} eur={eur} usd={usd} />}
        </>
      )}

      {/* ===== 3. THE HOOK — discount to market (distinct green) ===== */}
      {a.hasMarket && (
        <div className="rounded-[1.75rem] p-5 md:p-6 shadow-premium text-white bg-gradient-to-br from-green-500 to-emerald-600 mb-6 ring-1 ring-green-400/40">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <div>
              <h3 className="font-semibold text-sm opacity-95 flex items-center gap-2"><span>⚡</span> {t('instant_equity_below')}</h3>
              <p className="text-[11px] opacity-80">{t('day_one_gain')}</p>
            </div>
            <span className="text-3xl md:text-4xl font-extrabold tabular-nums">{pct(a.discountPct)}<span className="text-base font-medium"> {t('below')}</span></span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
            <div className="bg-white/10 rounded-xl p-3">
              <p className="text-xs opacity-80">{t('your_price')}</p>
              <p className="font-bold text-lg tabular-nums">{aedStr(a.price)}</p>
              <p className="text-[11px] opacity-75">{ilsStr(a.price * ils)}</p>
              {eur != null && <p className="text-[11px] opacity-75">{eurStr(a.price * eur)}</p>}
              {usd != null && <p className="text-[11px] opacity-75">{usdStr(a.price * usd)}</p>}
            </div>
            <div className="bg-white/10 rounded-xl p-3">
              <p className="text-xs opacity-80">{t('market_value')}</p>
              <p className="font-bold text-lg tabular-nums">{aedStr(a.marketValue)}</p>
              <p className="text-[11px] opacity-75">{ilsStr(a.marketValue * ils)}</p>
              {eur != null && <p className="text-[11px] opacity-75">{eurStr(a.marketValue * eur)}</p>}
              {usd != null && <p className="text-[11px] opacity-75">{usdStr(a.marketValue * usd)}</p>}
            </div>
            <div className="bg-white/20 rounded-xl p-3 col-span-2 md:col-span-1">
              <p className="text-xs opacity-90">{t('instant_equity_day1')}</p>
              <p className="font-extrabold text-xl tabular-nums"><CountUp value={a.discountAmount} format={aedStr} /></p>
              <p className="text-[11px] opacity-80">{ilsStr(a.discountAmount * ils)}</p>
              {eur != null && <p className="text-[11px] opacity-80">{eurStr(a.discountAmount * eur)}</p>}
              {usd != null && <p className="text-[11px] opacity-80">{usdStr(a.discountAmount * usd)}</p>}
            </div>
          </div>
        </div>
      )}

      {/* ROI headline cards + price per sqft — Advanced only (Lite: same numbers already
          live in the key-numbers strip / hero, so these would just repeat them) */}
      {!lite && (
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4 mb-6">
        <HeadlineStat label={t('capital_roi_handover')} value={a.roiAt(a.atHandover)} format={pct} accent="green"
          sub={t('keep_unit_ho')} />
        <HeadlineStat label={t('flip_handover_roi')} value={flip0 ? flip0.roi : a.flipROI} format={pct} accent="gold"
          sub={`${t('resell_at_handover')}${resale.enabled ? t('incl_resale_costs') : ''}`} />
        <HeadlineStat label={t('capital_roi_plus_y', { y: a.maturityYears })} value={a.roiAt(a.atMaturity)} format={pct} accent="green"
          sub={t('on_cash_deployed_x', { x: a.atMaturity ? a.atMaturity.year : t('maturity') })} />
        <HeadlineStat label={t('net_rental_yield_card')} value={a.netYield} format={pct} accent="navy"
          sub={t('gross_x_coc_y', { x: pct(a.grossYield), y: pct(a.cashYield) })} />
      </div>
      )}

      {!lite && (
      <div className="bg-white rounded-[1.75rem] p-5 shadow-premium border border-black/[0.06] mb-6">
        <h3 className="font-semibold text-gray-800 mb-3 text-sm">{t('price_per_sqft')}</h3>
        <div className="flex items-end justify-between">
          <div>
            <p className="text-2xl font-bold text-gray-900 tabular-nums">{a.totalSqft > 0 ? aedStr(a.pricePerSqft) : '—'}<span className="text-sm font-normal text-gray-400">{t('per_sqft_suffix')}</span>{a.totalSqft > 0 && <span className="text-sm font-normal text-gray-400"> · {perSqmStr(a.pricePerSqft)}</span>}</p>
            <p className="text-xs text-gray-400"><span className="tabular-nums">{a.totalSqft > 0 ? ilsStr(a.pricePerSqft * ils) + t('per_sqft_suffix') : ''}</span><span className="text-gray-300">{t('on_total_area')}</span></p>
            {eur != null && <p className="text-xs text-gray-400"><span className="tabular-nums">{a.totalSqft > 0 ? eurStr(a.pricePerSqft * eur) + t('per_sqft_suffix') : ''}</span></p>}
            {usd != null && <p className="text-xs text-gray-400"><span className="tabular-nums">{a.totalSqft > 0 ? usdStr(a.pricePerSqft * usd) + t('per_sqft_suffix') : ''}</span></p>}
          </div>
          <div className="text-right text-xs text-gray-400">
            <p>{t('x_total_sqft', { x: round(a.totalSqft).toLocaleString('en-GB') })}{a.totalSqft > a.internal ? t('incl_balcony') : ''}</p>
            {a.totalSqft > a.internal && <p>{t('x_internal_y_balcony', { i: round(a.internal).toLocaleString('en-GB'), b: round(a.balcony).toLocaleString('en-GB') })}</p>}
            {a.hasMarket && <p className="text-teal-500">{t('market_x_sqft', { x: aedStr(a.marketPricePerSqft) })}</p>}
          </div>
        </div>
      </div>
      )}

      {/* ===== 4. PAYMENT SCHEDULE ===== */}
      <div className="bg-white rounded-[1.75rem] p-4 md:p-5 shadow-premium border border-black/[0.06] mb-6">
        <h3 className="font-semibold text-gray-800 mb-4">{t('payment_schedule')}{unit.unit_number && <span className="ml-2 font-normal text-sm text-gray-400">· {t('unit_kw', { n: unit.unit_number })}</span>}</h3>

        {/* full milestone table — Advanced view only (Lite keeps just the summary cards) */}
        {!lite && (<>
        {/* desktop / tablet table */}
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full text-sm min-w-[640px]">
            <thead>
              <tr className="text-left text-gray-400 border-b border-black/[0.06]">
                <th className="py-2 pr-3 font-medium">{t('milestone')}</th>
                <th className="py-2 px-3 font-medium text-right">{t('pct_col')}</th>
                <th className="py-2 px-3 font-medium text-right">{t('amount')}</th>
                <th className="py-2 px-3 font-medium">{t('due')}</th>
                <th className="py-2 px-3 font-medium text-right">{t('cumulative')}</th>
                <th className="py-2 pl-3 font-medium">{t('cum_pct')}</th>
              </tr>
            </thead>
            <tbody>
              {a.schedule.map((r, i) => {
                const prev = a.schedule[i - 1];
                const showToday = !!(prev && prev.date < today && r.date >= today);
                return (
                  <Fragment key={i}>
                    {showToday && (
                      <tr aria-hidden="true">
                        <td colSpan={6} className="p-0">
                          <div className="flex items-center gap-2 py-1">
                            <div className="h-px flex-1 bg-rose-300" />
                            <span className="text-[10px] font-semibold text-rose-500 uppercase tracking-wide">{t('today')}</span>
                            <div className="h-px flex-1 bg-rose-300" />
                          </div>
                        </td>
                      </tr>
                    )}
                    <tr className={`border-b border-gray-50 transition-colors hover:bg-green-50/40 ${r.isFee ? 'bg-amber-50/50' : (i % 2 ? 'bg-gray-50/40' : '')}`}>
                      <td className="py-2 pr-3">
                        <span className={r.isFee ? 'text-amber-700' : 'text-gray-800'}>
                          {r.isFee ? '🧾 ' : ''}{tMilestone(r.label)}
                        </span>
                        {r.isHandover && <span className="ml-2 text-[10px] bg-green-100 text-green-700 px-1.5 py-0.5 rounded-full">{t('badge_handover')}</span>}
                        {r.isPost && <span className="ml-2 text-[10px] bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded-full" title={t('phpp_title')}>PHPP</span>}
                      </td>
                      <td className="py-2 px-3 text-right text-gray-500 tabular-nums">{r.pctVal != null ? pct(r.pctVal) : '—'}</td>
                      <td className="py-2 px-3 text-right tabular-nums"><Money aed={r.amount} ils={ils} eur={eur} usd={usd} /></td>
                      <td className="py-2 px-3 text-gray-500 whitespace-nowrap tabular-nums">{fmtMY(r.date)}</td>
                      <td className="py-2 px-3 text-right tabular-nums">
                        <div className="text-gray-700 font-medium">{aedStr(r.cumAED)}</div>
                        <div className="text-xs text-gray-400">{ilsStr(r.cumAED * ils)}</div>
                        {eur != null && <div className="text-xs text-gray-400">{eurStr(r.cumAED * eur)}</div>}
                        {usd != null && <div className="text-xs text-gray-400">{usdStr(r.cumAED * usd)}</div>}
                      </td>
                      <td className="py-2 pl-3">
                        <div className="flex items-center gap-2 justify-end">
                          <span className="text-gray-500 tabular-nums w-12 text-right">{pct(r.cumPct)}</span>
                          <div className="w-16 h-1.5 rounded-full bg-gray-100 overflow-hidden shrink-0">
                            <div className="h-full bg-primary rounded-full" style={{ width: `${Math.min(100, Math.max(0, r.cumPct))}%` }} />
                          </div>
                        </div>
                      </td>
                    </tr>
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* mobile stacked cards */}
        <div className="md:hidden space-y-2">
          {a.schedule.map((r, i) => {
            const prev = a.schedule[i - 1];
            const showToday = !!(prev && prev.date < today && r.date >= today);
            return (
              <div key={i}>
                {showToday && (
                  <div className="flex items-center gap-2 py-1.5">
                    <div className="h-px flex-1 bg-rose-300" />
                    <span className="text-[10px] font-semibold text-rose-500 uppercase tracking-wide">{t('today')}</span>
                    <div className="h-px flex-1 bg-rose-300" />
                  </div>
                )}
                <div className={`rounded-xl border p-3 ${r.isFee ? 'bg-amber-50/60 border-amber-100' : 'bg-white border-black/[0.06]'}`}>
                  <div className="flex justify-between items-start gap-2">
                    <div className="min-w-0">
                      <p className={`font-medium ${r.isFee ? 'text-amber-700' : 'text-gray-800'}`}>{r.isFee ? '🧾 ' : ''}{tMilestone(r.label)}
                        {r.isHandover && <span className="ml-1.5 text-[9px] bg-green-100 text-green-700 px-1.5 py-0.5 rounded-full">{t('badge_handover')}</span>}
                        {r.isPost && <span className="ml-1.5 text-[9px] bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded-full">PHPP</span>}
                      </p>
                      <p className="text-xs text-gray-400 mt-0.5"><span className="tabular-nums">{fmtMY(r.date)}</span> · {r.pctVal != null ? pct(r.pctVal) : t('fee')}</p>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="font-semibold text-gray-800 tabular-nums">{aedStr(r.amount)}</p>
                      <p className="text-[11px] text-gray-400 tabular-nums">{ilsStr(r.amount * ils)}</p>
                      {eur != null && <p className="text-[11px] text-gray-400 tabular-nums">{eurStr(r.amount * eur)}</p>}
                      {usd != null && <p className="text-[11px] text-gray-400 tabular-nums">{usdStr(r.amount * usd)}</p>}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 mt-2">
                    <div className="flex-1 h-1.5 rounded-full bg-gray-100 overflow-hidden">
                      <div className="h-full bg-primary rounded-full" style={{ width: `${Math.min(100, Math.max(0, r.cumPct))}%` }} />
                    </div>
                    <span className="text-[11px] text-gray-500 tabular-nums">{t('x_paid', { x: pct(r.cumPct) })}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        </>)}

        <div className={`grid grid-cols-1 gap-4 mt-5 ${a.hasPhpp ? 'sm:grid-cols-2 lg:grid-cols-5' : 'sm:grid-cols-3'}`}>
          <SummaryCard label={t('until_handover')} aed={a.bucketUntil} ils={ils} eur={eur} usd={usd} accent="amber" caption={feeCaption(a.feeParts?.until, lang)} />
          <SummaryCard label={t('on_handover')} aed={a.bucketOnHandover} ils={ils} eur={eur} usd={usd} accent="amber" caption={feeCaption(a.feeParts?.onHandover, lang)} />
          {a.hasPhpp && <SummaryCard label={t('post_handover_phpp')} aed={a.bucketPost} ils={ils} eur={eur} usd={usd} accent="blue" />}
          {a.hasPhpp && a.atPhppEnd && (() => {
            const grossByThen = isStr ? a.atPhppEnd.cumStrGross : a.atPhppEnd.cumGrossRent;
            const netByEnd = a.netOutOfPocketAt(a.atPhppEnd);
            return (
              <SummaryCard label={t('net_by_phpp_end')} aed={netByEnd} ils={ils} eur={eur} usd={usd} accent="blue"
                caption={t('net_by_phpp_end_caption', { rent: aedStr(grossByThen), year: a.phppEndYear })} />
            );
          })()}
          <SummaryCard label={t('grand_total_incl_fees')} aed={a.grandTotal} ils={ils} eur={eur} usd={usd} accent="gray" />
        </div>
      </div>

      {/* ===== 4b. CASH FLOW (period-by-period; both views) ===== */}
      <CashFlowSection a={a} ils={ils} eur={eur} usd={usd} view={view} altAnalysis={altAnalysis} />

      {/* ===== 5. PROJECTIONS: ROI table + flip ===== */}
      <div className={`grid gap-6 mb-6 ${lite ? 'grid-cols-1' : 'grid-cols-1 lg:grid-cols-3'}`}>
        {!lite && (
        <div className="bg-white rounded-[1.75rem] p-4 md:p-5 shadow-premium border border-black/[0.06] lg:col-span-2">
          <h3 className="font-semibold text-gray-800">{t('return_on_investment')}</h3>
          <p className="text-[11px] text-gray-400 mb-4">{t('roi_cash_note')}</p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[560px]">
              <thead>
                <tr className="text-left text-gray-400 border-b border-black/[0.06]">
                  <th className="py-2 pr-3 font-medium">{t('horizon')}</th>
                  <th className="py-2 px-3 font-medium text-right">{t('property_value')}</th>
                  <th className="py-2 px-3 font-medium text-right">{t('cash_deployed')}</th>
                  <th className="py-2 px-3 font-medium text-right">{t('capital_roi')}</th>
                  <th className="py-2 px-3 font-medium text-right">{t('cum_net_rent')}</th>
                  <th className="py-2 pl-3 font-medium text-right">{t('combined_roi')}</th>
                </tr>
              </thead>
              <tbody>
                {[[t('at_handover'), a.atHandover], [t('plus_y_midpoint', { y: a.midYears }), a.atMid], [t('plus_y_maturity', { y: a.maturityYears }), a.atMaturity]].map(([lbl, row]) => row && (
                  <tr key={lbl} className="border-b border-gray-50">
                    <td className="py-2 pr-3 text-gray-800 font-medium">{lbl}</td>
                    <td className="py-2 px-3 text-right tabular-nums"><Money aed={row.value} ils={ils} eur={eur} usd={usd} /></td>
                    <td className="py-2 px-3 text-right text-gray-500 tabular-nums">{aedStr(a.cashDeployedAt(row))}</td>
                    <td className="py-2 px-3 text-right text-green-600 font-medium tabular-nums">{pct(a.roiAt(row))}</td>
                    <td className="py-2 px-3 text-right text-gray-700 tabular-nums">{aedStr(row.cumNetRent)}</td>
                    <td className="py-2 pl-3 text-right text-blue-600 font-semibold tabular-nums">{pct(a.combinedAt(row))}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mt-4 text-sm">
            <MiniStat label={t('gross_yield_rp')} value={pct(a.grossYield)} />
            <MiniStat label={t('net_yield_nc')} value={pct(a.netYield)} />
            <MiniStat label={t('coc_yield')} value={pct(a.cashYield)}
              caption={t('coc_caption')}
              hint={t('coc_hint')} />
            <MiniStat label={t('combined_roi_ann')} value={pct(a.combinedMaturityAnnual)} />
            <MiniStat label={t('break_even')} value={a.breakEven ? t('yr_x', { x: a.breakEven.year }) : t('gt_x_yr', { x: a.maturityYears })} />
          </div>

          {/* Informational only — not part of any ROI. True net cash still out of pocket
              after banking gross rent = cash deployed + service charges paid − gross rent. */}
          <div className="mt-4 border-t border-black/[0.06] pt-3">
            <p className="text-[11px] text-gray-400 mb-2">
              {t('net_oop_title')}
              <span className="text-gray-300">{t('net_oop_note')}</span>
            </p>
            <div className="grid grid-cols-3 gap-3 text-sm">
              {[[t('at_handover'), a.atHandover], [t('plus_yy', { y: a.midYears }), a.atMid], [t('plus_yy', { y: a.maturityYears }), a.atMaturity]].map(([lbl, row]) => row && (
                <div key={lbl} className="bg-gray-50 rounded-xl p-2.5 text-center">
                  <p className="text-[11px] text-gray-400 mb-0.5">{lbl}</p>
                  <p className="font-semibold text-gray-600 tabular-nums">{aedStr(a.netOutOfPocketAt(row))}</p>
                </div>
              ))}
            </div>
          </div>

          {a.hasMarket && (
            <div className="mt-4 border-t border-black/[0.06] pt-4">
              <p className="text-xs text-gray-400 mb-2">{t('appr_breakdown')}</p>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="bg-teal-50 rounded-xl p-3">
                  <p className="text-xs text-teal-600">{t('instant_equity_discount')}</p>
                  <p className="font-bold text-teal-700 tabular-nums">{aedStr(a.instantEquity)}</p>
                  <p className="text-[11px] text-teal-500">{t('x_of_total_cost', { x: a.totalCost > 0 ? pct(a.instantEquity / a.totalCost * 100) : '—' })}</p>
                </div>
                <div className="bg-emerald-50 rounded-xl p-3">
                  <p className="text-xs text-emerald-600">{t('market_appreciation')}</p>
                  <p className="font-bold text-emerald-700 tabular-nums">{aedStr((a.atMaturity?.marketAppr) || 0)}</p>
                  <p className="text-[11px] text-emerald-500">{t('x_of_total_cost', { x: a.totalCost > 0 ? pct(((a.atMaturity?.marketAppr) || 0) / a.totalCost * 100) : '—' })}</p>
                </div>
              </div>
            </div>
          )}
        </div>
        )}

        <div id="exit-scenario-card">
          <ExitScenarioCard a={a} ils={ils} eur={eur} usd={usd} resale={resale} setResale={setResale} n={exitN} setN={setExitN} />
        </div>
      </div>

      {/* ===== 5b. CHARTS (Lite: growth chart only — payment chart repeats the
          payment-schedule summary cards above, so it's Advanced-only) ===== */}
      <div className={`grid gap-6 ${lite ? 'grid-cols-1' : 'grid-cols-1 lg:grid-cols-2'}`}>
        {!lite && (
        <ChartCard title={t('cumulative_payments')} subtitle={t('money_in_over_time')}>
          {a.paymentChart.length ? (
            <ResponsiveContainer width="100%" height={260}>
              <AreaChart data={a.paymentChart} margin={{ top: 8, right: 12, left: 0, bottom: 5 }}>
                <defs>
                  <linearGradient id="gPay" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={C.payment} stopOpacity={0.4} />
                    <stop offset="100%" stopColor={C.payment} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey={a.hasDates ? 'dateLabel' : 'name'} tick={{ fontSize: 10 }} interval={0} angle={-25} textAnchor="end" height={60} />
                <YAxis tick={{ fontSize: 11 }} tickFormatter={compact} width={44} />
                <Tooltip content={<AedIlsTooltip ils={ils} eur={eur} usd={usd} />} />
                {payHandoverX != null && <ReferenceLine x={payHandoverX} stroke={C.handover} strokeDasharray="4 3" label={handoverLabel} />}
                <Area type="stepAfter" dataKey="cumulative" name={t('cumulative_paid')} stroke={C.payment} strokeWidth={2} fill="url(#gPay)" isAnimationActive={false} />
              </AreaChart>
            </ResponsiveContainer>
          ) : <ChartEmpty />}
        </ChartCard>
        )}

        <ChartCard title={t('property_value_vs_invested')}>
          {a.years.length ? (
            <ResponsiveContainer width="100%" height={260}>
              <LineChart data={a.years} margin={{ top: 8, right: 12, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="year" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} tickFormatter={compact} width={44} />
                <Tooltip content={<AedIlsTooltip ils={ils} eur={eur} usd={usd} />} />
                <Legend wrapperStyle={{ fontSize: 12 }} align={legendAlign} />
                {handoverYear != null && <ReferenceLine x={handoverYear} stroke={C.handover} strokeDasharray="4 3" label={handoverLabel} />}
                <Line type="monotone" dataKey="value" name={t('property_value_series')} stroke={C.value} strokeWidth={2} dot={false} isAnimationActive={false} />
                <Line type="monotone" dataKey="invested" name={t('total_invested')} stroke={C.invested} strokeWidth={2} dot={false} strokeDasharray="5 4" isAnimationActive={false} />
              </LineChart>
            </ResponsiveContainer>
          ) : <ChartEmpty />}
        </ChartCard>

        {!lite && (<>
        <ChartCard title={t('annual_gains')}>
          {gainsData.length ? (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={gainsData} margin={{ top: 8, right: 12, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="year" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} tickFormatter={compact} width={44} />
                <Tooltip content={<AedIlsTooltip ils={ils} eur={eur} usd={usd} />} />
                <Legend wrapperStyle={{ fontSize: 12 }} align={legendAlign} />
                <Bar dataKey="netRent" name={t('net_rent')} stackId="a" fill={C.rent} isAnimationActive={false} />
                {a.hasMarket && <Bar dataKey="instantEquity" name={t('instant_equity_discount')} stackId="a" fill={C.equity} isAnimationActive={false} />}
                <Bar dataKey="marketAppr" name={a.hasMarket ? t('market_appreciation') : t('appreciation_cum')} stackId="a" fill={C.appreciation} radius={[4, 4, 0, 0]} isAnimationActive={false} />
              </BarChart>
            </ResponsiveContainer>
          ) : <ChartEmpty />}
        </ChartCard>

        <ChartCard title={t('combined_roi_over_time')} subtitle={(a.breakEven ? t('break_even_year_x', { x: a.breakEven.year }) : '') + t('on_cash_each_point')}>
          {a.years.length ? (
            <ResponsiveContainer width="100%" height={260}>
              <LineChart data={a.years} margin={{ top: 8, right: 12, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="year" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} tickFormatter={v => v.toFixed(0) + '%'} width={44} />
                <Tooltip content={<PctTooltip />} />
                {handoverYear != null && <ReferenceLine x={handoverYear} stroke={C.handover} strokeDasharray="4 3" label={handoverLabel} />}
                <ReferenceLine y={0} stroke="#e5e7eb" />
                <Line type="monotone" dataKey="combinedROI" name={t('combined_roi_series')} stroke={C.roi} strokeWidth={2.5} dot={false} isAnimationActive={false} />
                {a.breakEven && (
                  <ReferenceDot x={a.breakEven.year} y={a.breakEven.combinedROI} r={5} fill={C.roi} stroke="#fff" strokeWidth={2}
                    label={{ value: t('break_even'), position: 'top', fontSize: 10, fill: C.roi, fontWeight: 600 }} />
                )}
              </LineChart>
            </ResponsiveContainer>
          ) : <ChartEmpty />}
        </ChartCard>
        </>)}
      </div>

      {/* ===== 5c. SENSITIVITY (Advanced only; A4 PDF included, mobile PDF skipped) ===== */}
      {!lite && (
        <div className="mt-6">
          <SensitivityMatrices project={project} unit={unit} engineOpts={engineOpts || assumptions} resale={resale} exitN={exitN} ils={ils} eur={eur} usd={usd} />
        </div>
      )}

      {/* ===== 6. ASSUMPTIONS (collapsed by default, Advanced only) ===== */}
      {showSummary && !lite && (
        <details className="mt-6 bg-white rounded-[1.75rem] border border-black/[0.06] shadow-premium group">
          <summary className="cursor-pointer list-none px-5 py-4 flex items-center justify-between text-sm font-semibold text-gray-700 select-none">
            <span>{t('assumptions_fees_used')}</span>
            <span className="text-gray-300 text-xs group-open:rotate-180 transition-transform">▼</span>
          </summary>
          <div className="px-5 pb-5 grid grid-cols-1 lg:grid-cols-2 gap-6 border-t border-gray-50 pt-4">
            <div>
              <h4 className="font-semibold text-gray-800 mb-3 text-sm">{t('fees')}</h4>
              <dl className="text-sm space-y-2">
                <div className="flex justify-between"><dt className="text-gray-500">{a.emirate === 'rak' ? t('dld_at_handover', { r: a.dldRate }) : t('dld_at_20', { r: a.dldRate })}</dt><dd className="font-medium text-gray-800">{a.dldAmount > 0 ? aedStr(a.dldAmount) : t('paid_by_developer')}</dd></div>
                <div className="flex justify-between"><dt className="text-gray-500">{t('admin_fee_at_booking')}</dt><dd className="font-medium text-gray-800">{aedStr(a.admin)}</dd></div>
                <div className="flex justify-between"><dt className="text-gray-500">{t('service_charge_from_ho')}</dt><dd className="font-medium text-gray-800 tabular-nums">{aedStr(a.scYearly)}{t('per_yr')}</dd></div>
              </dl>
            </div>
            <div>
              <h4 className="font-semibold text-gray-800 mb-3 text-sm">{t('projection_assumptions')}</h4>
              <dl className="text-sm space-y-2">
                <div className="flex justify-between"><dt className="text-gray-500">{t('cap_appr_bands')}</dt><dd className="font-medium text-gray-800 tabular-nums">{(assumptions?.capBands || []).join(' / ')}</dd></div>
                <div className="flex justify-between"><dt className="text-gray-500">{t('rent_appr_bands')}</dt><dd className="font-medium text-gray-800 tabular-nums">{(assumptions?.rentBands || []).join(' / ')}</dd></div>
                <div className="flex justify-between"><dt className="text-gray-500">{t('starting_rent_yearly')}</dt><dd className="font-medium text-gray-800">{aedStr(a.startRent)}</dd></div>
                <div className="flex justify-between"><dt className="text-gray-500">{t('projection_horizon')}</dt><dd className="font-medium text-gray-800">{t('x_years_from_handover', { x: a.maturityYears })}</dd></div>
                {a.hasMarket && <div className="flex justify-between"><dt className="text-gray-500">{t('market_value_base')}</dt><dd className="font-medium text-gray-800">{aedStr(a.marketValue)}</dd></div>}
              </dl>
            </div>
          </div>
        </details>
      )}
    </>
  );
}
